import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fornecedorService, type FiltroFornecedores } from '../services/fornecedorService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarFornecedorRequest, AtualizarFornecedorRequest } from '../types';

interface UseFornecedorParams {
  pagina?: number;
  tamanhoPagina?: number;
  apenasAtivos?: boolean;
}

export const useFornecedor = ({ pagina = 1, tamanhoPagina = 20, apenasAtivos = true }: UseFornecedorParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['fornecedor', pagina, tamanhoPagina, apenasAtivos],
    queryFn: () => fornecedorService.getAll(pagina, tamanhoPagina, apenasAtivos),
  });

  const fornecedores  = paginado?.itens       ?? [];
  const total         = paginado?.total        ?? 0;
  const totalPaginas  = paginado?.totalPaginas ?? 1;

  const createMutation = useMutation({
    mutationFn: (request: CriarFornecedorRequest) => fornecedorService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedor'] });
      toast.success('Fornecedor cadastrado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarFornecedorRequest }) =>
      fornecedorService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedor'] });
      toast.success('Fornecedor atualizado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    fornecedores,
    total,
    totalPaginas,
    isLoading,
    isFetching,
    criarFornecedor:    createMutation.mutateAsync,
    atualizarFornecedor: updateMutation.mutateAsync,
    isCreating:  createMutation.isPending,
    isUpdating:  updateMutation.isPending,
  };
};

/** Consulta paginada no servidor + KPIs do filtro. Mantém a lista anterior enquanto recarrega. */
export const useFornecedoresConsulta = (filtro: FiltroFornecedores) => {
  const lista = useQuery({
    queryKey: ['fornecedor', 'consulta', filtro],
    queryFn: () => fornecedorService.listar(filtro),
    placeholderData: keepPreviousData,
  });
  const resumo = useQuery({
    queryKey: ['fornecedor', 'resumo', filtro.busca, filtro.dtInicio, filtro.dtFim],
    queryFn: () => fornecedorService.resumo(filtro),
    placeholderData: keepPreviousData,
  });
  return { lista, resumo };
};

export const useFornecedorDetalhe = (id: number) => {
  return useQuery({
    queryKey: ['fornecedor', id],
    queryFn: () => fornecedorService.getById(id),
    enabled: id > 0,
  });
};

export const useFornecedorBusca = (q: string, enabled = true) => {
  return useQuery({
    queryKey: ['fornecedor', 'busca', q],
    queryFn: () => fornecedorService.buscar(q),
    enabled: enabled && q.length >= 2,
  });
};
